import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useRoleAccess } from "../hooks/useRoleAccess";
import { UserRole } from "../types/role";

interface ProtectedRouteProps {
  children: React.ReactNode;
  uid?: string;
  requiredRole?: UserRole;
  minimumRole?: UserRole;
  requiredRoles?: UserRole[];
  canAccessDebugMenu?: boolean;
  canAccessDebugEvents?: boolean;
  canAccessDebugThreads?: boolean;
  canAccessDebugAgents?: boolean;
  canAccessDebugPlugins?: boolean;
  fallback?: React.ReactNode;
  redirectTo?: string;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  uid,
  requiredRole,
  minimumRole,
  requiredRoles,
  canAccessDebugMenu,
  canAccessDebugEvents,
  canAccessDebugThreads,
  canAccessDebugAgents,
  canAccessDebugPlugins,
  fallback,
  redirectTo = "/",
}) => {
  const location = useLocation();
  const {
    userRole,
    loading,
    error,
    canAccessDebugMenu: userCanAccessDebugMenu,
    canAccessDebugEvents: userCanAccessDebugEvents,
    canAccessDebugThreads: userCanAccessDebugThreads,
    canAccessDebugAgents: userCanAccessDebugAgents,
    canAccessDebugPlugins: userCanAccessDebugPlugins,
    hasRole,
    hasMinimumRole,
  } = useRoleAccess({ uid });

  // Show loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <p className="text-gray-600">Verifying access permissions...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Access Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button onClick={() => window.location.reload()} className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600">
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Check if user is authenticated
  if (!uid || !userRole) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Check only the access requirements that are actually needed
  const hasRequiredRole = !requiredRole || hasRole(requiredRole);
  const hasMinimumRoleAccess = !minimumRole || hasMinimumRole(minimumRole);
  const hasRequiredRoles = !requiredRoles || requiredRoles.length === 0 || requiredRoles.some((role) => hasRole(role));
  const hasDebugMenuAccess = !canAccessDebugMenu || userCanAccessDebugMenu;
  const hasDebugEventsAccess = !canAccessDebugEvents || userCanAccessDebugEvents;
  const hasDebugThreadsAccess = !canAccessDebugThreads || userCanAccessDebugThreads;
  const hasDebugAgentsAccess = !canAccessDebugAgents || userCanAccessDebugAgents;
  const hasDebugPluginsAccess = !canAccessDebugPlugins || userCanAccessDebugPlugins;

  // Only check the permissions that are actually required for this route
  const requiredChecks = [
    hasRequiredRole,
    hasMinimumRoleAccess,
    hasRequiredRoles,
    ...(canAccessDebugMenu !== undefined ? [hasDebugMenuAccess] : []),
    ...(canAccessDebugEvents !== undefined ? [hasDebugEventsAccess] : []),
    ...(canAccessDebugThreads !== undefined ? [hasDebugThreadsAccess] : []),
    ...(canAccessDebugAgents !== undefined ? [hasDebugAgentsAccess] : []),
    ...(canAccessDebugPlugins !== undefined ? [hasDebugPluginsAccess] : []),
  ];

  // If any required check fails, show access denied
  if (requiredChecks.some((check) => !check)) {
    if (fallback) return <>{fallback}</>;
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h2>
          <p className="text-gray-600 mb-4">You don&apos;t have permission to access this page.</p>
        </div>
      </div>
    );
  }

  // Access granted
  return <>{children}</>;
};

// Convenience components for common access patterns
export const AdminRoute: React.FC<Omit<ProtectedRouteProps, "requiredRole">> = (props) => <ProtectedRoute {...props} requiredRole="admin" />;

export const TesterRoute: React.FC<Omit<ProtectedRouteProps, "minimumRole">> = (props) => <ProtectedRoute {...props} minimumRole="tester" />;

export const BetaRoute: React.FC<Omit<ProtectedRouteProps, "minimumRole">> = (props) => <ProtectedRoute {...props} minimumRole="beta" />;

export const DebugRoute: React.FC<Omit<ProtectedRouteProps, "canAccessDebugMenu">> = (props) => <ProtectedRoute {...props} canAccessDebugMenu={true} />;

export const DebugEventsRoute: React.FC<Omit<ProtectedRouteProps, "canAccessDebugEvents">> = (props) => <ProtectedRoute {...props} canAccessDebugEvents={true} />;

export const DebugThreadsRoute: React.FC<Omit<ProtectedRouteProps, "canAccessDebugThreads">> = (props) => <ProtectedRoute {...props} canAccessDebugThreads={true} />;

export const DebugAgentsRoute: React.FC<Omit<ProtectedRouteProps, "canAccessDebugAgents">> = (props) => <ProtectedRoute {...props} canAccessDebugAgents={true} />;

export const DebugPluginsRoute: React.FC<Omit<ProtectedRouteProps, "canAccessDebugPlugins">> = (props) => <ProtectedRoute {...props} canAccessDebugPlugins={true} />;
